import { useCallback, useMemo } from "react";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import {
  OplBoard,
  OplItem,
  OplItemPriority,
  OplItemType,
  OplMeeting,
  OplPromotionResult,
  OplSummaryResult,
} from "src/questionnaires";

/**
 * Typed client for the OPL routes (`/api/v2/opsanio/opl/...`).
 *
 * Normalises two things so no component has to know about them: the backend's
 * `{ status, data }` envelope, and AnketaApiResponse rewriting an empty payload
 * to `status: 204, data: null` with `success` still true.
 *
 * Nothing here throws. `409 ABSENT_OWNER` is surfaced as `absentOwner` rather
 * than as a failure, because it is a question to the operator, not an error.
 */

export interface OplCallResult<T> {
  ok: boolean;
  data?: T;
  status: number;
  error?: string;
  unavailable?: boolean;
  conflict?: boolean;
  /** The assignment target is absent without a substitute; confirm to proceed. */
  absentOwner?: boolean;
}

export interface OplAttendeeInput {
  email: string;
  name?: string;
  source?: "keycloak" | "contact" | "manual";
  present?: boolean;
  isGuest?: boolean;
  organisation?: string;
  fromTime?: string;
  untilTime?: string;
  absenceReason?: "excused" | "unexcused";
  substitute?: string;
}

export interface OplMeetingInput {
  date?: string;
  title?: string;
  notes?: string;
  status?: OplMeeting["status"];
  durationMinutes?: number;
  recorder?: string;
  moderator?: string;
  attendees?: OplAttendeeInput[];
}

export interface OplItemInput {
  title: string;
  type?: OplItemType;
  owner?: string;
  dueDate?: string;
  priority?: OplItemPriority;
  meetingId?: string;
  acknowledgeAbsence?: boolean;
}

export interface OplItemPatch {
  title?: string;
  owner?: string;
  dueDate?: string;
  priority?: OplItemPriority;
  meetingId?: string;
  logText?: string;
  acknowledgeAbsence?: boolean;
}

const BASE = `v2/${AnketaResourcesEnum.opsanio}/opl`;

export const useOplApi = (applianceName: string) => {
  const { Get, Post, Put, Delete } = useApi();
  const { keycloak } = useAnketaAuth();

  const token = keycloak?.token;
  const canCall = typeof token === "string" && applianceName.trim() !== "";
  const appliance = encodeURIComponent(applianceName.trim());

  const unwrap = useCallback(
    <T,>(response: {
      success: boolean;
      status: number;
      data?: any;
      error?: any;
    }): OplCallResult<T> => {
      const status = response.status;
      if (!response.success || response.error) {
        const details = response.error?.details ?? response.error?.service;
        const message =
          details?.message ?? response.error?.message ?? "Die Anfrage ist fehlgeschlagen.";
        return {
          ok: false,
          status,
          error: message,
          unavailable: status === 503,
          conflict: status === 409,
          absentOwner: status === 409 && details?.code === "ABSENT_OWNER",
        };
      }
      return { ok: true, status, data: (response.data?.data ?? undefined) as T };
    },
    [],
  );

  return useMemo(() => {
    const call = async <T,>(run: () => Promise<any>): Promise<OplCallResult<T>> => {
      if (!canCall) return { ok: false, status: 0, error: "Not ready" };
      return unwrap<T>(await run());
    };

    const json = (path: string, value?: unknown) => ({
      path,
      value,
      responseType: "json" as const,
      token,
    });

    return {
      canCall,

      /** Everything the board needs in one round trip. */
      board: () => call<OplBoard>(() => Get(json(`${BASE}/board/${appliance}`))),

      createMeeting: (value: OplMeetingInput) =>
        call<OplMeeting>(() => Post(json(`${BASE}/meetings/${appliance}`, value))),

      advanceRound: () =>
        call<OplMeeting>(() => Post(json(`${BASE}/meetings/${appliance}/advance`, {}))),

      updateMeeting: (id: string, value: OplMeetingInput) =>
        call<OplMeeting>(() =>
          Put(json(`${BASE}/meeting/${encodeURIComponent(id)}`, value)),
        ),

      deleteMeeting: (id: string) =>
        call<{ id: string }>(() =>
          Delete(json(`${BASE}/meeting/${encodeURIComponent(id)}`)),
        ),

      sendSummary: (id: string, value: { intro?: string; markHeld?: boolean } = {}) =>
        call<OplSummaryResult>(() =>
          Post(json(`${BASE}/meeting/${encodeURIComponent(id)}/summary`, value)),
        ),

      createItem: (value: OplItemInput) =>
        call<OplItem>(() => Post(json(`${BASE}/items/${appliance}`, value))),

      updateItem: (id: string, value: OplItemPatch) =>
        call<OplItem>(() => Put(json(`${BASE}/item/${encodeURIComponent(id)}`, value))),

      appendLog: (id: string, text: string, meetingId?: string) =>
        call<OplItem>(() =>
          Post(json(`${BASE}/item/${encodeURIComponent(id)}/log`, { text, meetingId })),
        ),

      closeItem: (
        id: string,
        status: "done" | "discarded",
        text?: string,
        meetingId?: string,
      ) =>
        call<OplItem>(() =>
          Post(
            json(`${BASE}/item/${encodeURIComponent(id)}/close`, {
              status,
              text,
              meetingId,
            }),
          ),
        ),

      reopenItem: (id: string, text: string, meetingId?: string) =>
        call<OplItem>(() =>
          Post(json(`${BASE}/item/${encodeURIComponent(id)}/reopen`, { text, meetingId })),
        ),

      splitItem: (id: string, titles: string[], meetingId?: string) =>
        call<OplItem>(() =>
          Post(json(`${BASE}/item/${encodeURIComponent(id)}/split`, { titles, meetingId })),
        ),

      deleteItem: (id: string, force = false) =>
        call<{ id: string }>(() =>
          Delete(
            json(`${BASE}/item/${encodeURIComponent(id)}${force ? "?force=1" : ""}`),
          ),
        ),

      promoteToGitlab: (id: string) =>
        call<OplPromotionResult>(() =>
          Post(json(`${BASE}/item/${encodeURIComponent(id)}/promote/gitlab`, {})),
        ),

      promoteToOtobo: (id: string) =>
        call<OplPromotionResult>(() =>
          Post(json(`${BASE}/item/${encodeURIComponent(id)}/promote/otobo`, {})),
        ),
    };
    // Get/Post/Put/Delete are excluded on purpose: useApi() returns new function
    // identities every render, so including them would rebuild this client - and
    // re-run every effect depending on it - on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliance, canCall, token, unwrap]);
};

/**
 * The cross-appliance overview: every OPEN point of every appliance the caller
 * may read (the server filters - operators see all, members their own).
 */
export const useOplOverviewApi = () => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const token = keycloak?.token;

  return useMemo(
    () => ({
      canCall: typeof token === "string",
      openItems: async (): Promise<OplCallResult<OplItem[]>> => {
        if (typeof token !== "string") return { ok: false, status: 0, error: "Not ready" };
        const response = await Get<any>({
          path: `${BASE}/open`,
          responseType: "json",
          token,
        });
        if (!response.success || response.error) {
          return {
            ok: false,
            status: response.status,
            error: response.error?.message ?? "Die Anfrage ist fehlgeschlagen.",
            unavailable: response.status === 503,
          };
        }
        // An empty list arrives as 204 / data null.
        const data = (response.data as any)?.data;
        return { ok: true, status: response.status, data: Array.isArray(data) ? data : [] };
      },
    }),
    // Get is a fresh function every render; token decides readiness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token],
  );
};
